/**
 * OneSignal push notification integration.
 *
 * Two modes:
 * - Native (Capacitor): Uses @onesignal/capacitor-plugin via FCM/APNs
 * - Web (Browser): Uses OneSignal Web SDK loaded via script tag in index.html
 *
 * Automatically picks the right one based on environment.
 *
 * The native side was previously onesignal-cordova-plugin, reached through the
 * `window.plugins.OneSignal` global that Cordova injects. That plugin declares
 * its iOS SDK as a CocoaPod, which the SPM-based iOS build cannot satisfy, so
 * it was stripped from the iOS Package.swift and every iOS build silently fell
 * through to the web branch — where WKWebView has no Push API, so no iOS build
 * could ever deliver a notification. @onesignal/capacitor-plugin ships a
 * Package.swift with a remote SPM dependency on OneSignal-XCFramework, so it
 * resolves in the same build that could not resolve the Cordova one.
 */

import { Capacitor } from '@capacitor/core';
import OneSignalNative from '@onesignal/capacitor-plugin';

const ONESIGNAL_APP_ID = '4ccdf9d0-78cb-4c7e-a87f-370b4c881470';

let initialized = false;
let isNative = false;
let webReady: Promise<any> | null = null;
let resolveWebReady: ((os: any) => void) | null = null;

// The native subscription id arrives asynchronously (getIdAsync), but
// getOneSignalSubscriptionId() is a synchronous getter that callers already
// depend on. Cache the latest value from the SDK's own change event so the
// getter keeps its shape on both platforms.
let nativeSubscriptionId: string | null = null;

/** Run a function once the web SDK is ready */
async function whenWebReady(fn: (os: any) => Promise<void>) {
  if (!webReady) return;
  try {
    const os = await webReady;
    if (os) await fn(os);
  } catch (e) {
    console.warn('[OneSignal] Web SDK error:', e);
  }
}

/** Initialize OneSignal SDK — call once on app start */
export function initOneSignal() {
  if (initialized) return;

  // Native (Capacitor) — decided by the platform rather than by probing for an
  // injected global, so a plugin that failed to compile in cannot masquerade as
  // "this is a browser" and silently downgrade to the web SDK.
  if (Capacitor.isNativePlatform()) {
    isNative = true;
    initialized = true;

    // initialize() is async here (the Cordova plugin's was fire-and-forget).
    // Permission is requested only after it resolves, because requesting before
    // the SDK is up is what produced a permission prompt with no subscription
    // behind it.
    OneSignalNative.initialize(ONESIGNAL_APP_ID)
      .then(async () => {
        console.log('[OneSignal] Initialized (native)');

        // Keep the cached id fresh. Registered before requestPermission so the
        // first id the SDK mints is captured rather than raced past.
        try {
          OneSignalNative.User.pushSubscription.addEventListener('change', (event: any) => {
            nativeSubscriptionId = event?.current?.id ?? null;
          });
        } catch (e) {
          console.warn('[OneSignal] Could not observe push subscription:', e);
        }

        try {
          await OneSignalNative.Notifications.requestPermission(true);
        } catch (e) {
          console.warn('[OneSignal] requestPermission failed:', e);
        }

        try {
          nativeSubscriptionId = await OneSignalNative.User.pushSubscription.getIdAsync();
        } catch (e) {
          console.warn('[OneSignal] Could not read push subscription id:', e);
        }
      })
      .catch((e) => {
        console.warn('[OneSignal] Native init failed:', e);
      });

    return;
  }

  // Web SDK — init is handled in index.html via OneSignalDeferred
  // We just need to wait for it and grab the instance
  webReady = new Promise((resolve) => { resolveWebReady = resolve; });

  (window as any).OneSignalDeferred = (window as any).OneSignalDeferred || [];
  (window as any).OneSignalDeferred.push(async (OneSignal: any) => {
    // This runs after init completes (queued after the HTML init call)
    console.log('[OneSignal] Web SDK ready');
    resolveWebReady?.(OneSignal);
  });

  initialized = true;
  isNative = false;
}

/** Login player after auth */
export function loginOneSignal(playerGUID: string, email?: string, phone?: string) {
  if (!initialized) return;

  if (isNative) {
    try {
      OneSignalNative.login(playerGUID);
      if (email) OneSignalNative.User.addEmail(email);
      if (phone) OneSignalNative.User.addSms(phone);
      console.log('[OneSignal] Logged in as', playerGUID, '(native)');
    } catch (e) {
      console.warn('[OneSignal] Native login failed:', e);
    }
  } else {
    whenWebReady(async (OneSignal) => {
      await OneSignal.login(playerGUID);
      if (email) await OneSignal.User.addEmail(email);
      if (phone) await OneSignal.User.addSms(phone);
      console.log('[OneSignal] Logged in as', playerGUID, '(web)');

      // Request push permission after login
      await OneSignal.Notifications.requestPermission();
    });
  }
}

/** Get the push subscription ID for backend storage */
export function getOneSignalSubscriptionId(): string | null {
  if (!initialized) return null;

  try {
    if (isNative) {
      return nativeSubscriptionId;
    }
    return (window as any).OneSignal?.User?.PushSubscription?.id || null;
  } catch (e) {
    return null;
  }
}

/** Store the device subscription with the backend */
export function storeOneSignalDevice(socket: any, playerGUID: string) {
  if (!initialized || !socket) return;

  // Delay slightly to allow subscription to register
  setTimeout(() => {
    try {
      const subscriptionId = getOneSignalSubscriptionId();
      if (subscriptionId) {
        socket.emit('storeOneSignalDevice', {
          playerGuid: playerGUID,
          oneSignalUser: {
            PushSubscription: { Id: subscriptionId }
          }
        });
        console.log('[OneSignal] Device stored:', subscriptionId);
      } else {
        console.log('[OneSignal] No push subscription ID yet');
      }
    } catch (e) {
      console.warn('[OneSignal] Failed to store device:', e);
    }
  }, 2000);
}

/** Add an in-app message trigger */
export function addOneSignalTrigger(key: string, value: string) {
  if (!initialized) return;

  if (isNative) {
    try {
      OneSignalNative.InAppMessages.addTrigger(key, value);
    } catch (e) {
      console.warn('[OneSignal] Failed to add trigger:', e);
    }
  } else {
    whenWebReady(async (OneSignal) => {
      OneSignal.InAppMessages?.addTrigger(key, value);
    });
  }
}

/** Logout — clear OneSignal user */
export function logoutOneSignal() {
  if (!initialized) return;

  if (isNative) {
    try {
      OneSignalNative.logout();
      console.log('[OneSignal] Logged out (native)');
    } catch (e) {
      console.warn('[OneSignal] Failed to logout:', e);
    }
  } else {
    whenWebReady(async (OneSignal) => {
      await OneSignal.logout();
      console.log('[OneSignal] Logged out (web)');
    });
  }
}
